import { useEffect, useState } from "react";
import {
  FiPhone,
  FiUser,
  FiMail,
  FiMapPin,
  FiX,
  FiCalendar,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import { HiArrowSmLeft } from "react-icons/hi";
import { MdShoppingCart, MdPayment } from "react-icons/md";
import { FaDirections, FaFileInvoice } from "react-icons/fa";
import { httpService } from "../../services/httpService";
import { tokenDecode } from "../../utils/helper";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ProductCatalogDrawer from "./ProductCatalogDrawer";
import OrderPaymentDrawer from "../../components/Order/OrderPaymentDrawer";
import PaymentAndInvoice from "../common/PaymentAndInvoice";

const POSDetailsDrawer = ({
  isOpen = false,
  onClose = () => {},
  orderId = null,
  mode = "edit", // "edit" or "view"
  refreshClients,
}) => {
  const [orderData, setOrderData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [showProductCatalog, setShowProductCatalog] = useState(false);
  const [paymentOrderId, setPaymentOrderId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Track changes for temporary state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalProductList, setOriginalProductList] = useState([]);
  const [originalDiscount, setOriginalDiscount] = useState({
    type: "percentage",
    value: 0,
    amount: 0
  });

  const [discount, setDiscount] = useState({
    type: "percentage",
    value: 0,
    amount: 0
  });

  const [totalSummary, setTotalSummary] = useState({
    subtotal: 0,
    gst: 0,
    total: 0,
    grandTotal: 0,
  });

  // Update isReadOnly logic to consider both payment status AND mode
  const isOrderPaid = orderData?.payment_status === "paid";
  const isOrderCancelled = (orderData?.status || "").toString().toLowerCase().includes("cancel");
  
  // Read-only if: paid order OR cancelled order OR mode is "view"
  const isReadOnly = isOrderPaid || isOrderCancelled || mode === "view";

  // Calculate totals whenever productList or discount changes
  useEffect(() => {
    if (orderData) {
      calculateTotals();
    }
  }, [productList, discount.value, discount.type, orderData]);

  // Track changes for temporary state - disable tracking if read-only
  useEffect(() => {
    if (isReadOnly) {
      setHasUnsavedChanges(false);
      return;
    }

    if (productList.length > 0 || discount.value !== 0) {
      const productsChanged = JSON.stringify(productList) !== JSON.stringify(originalProductList);
      const discountChanged = JSON.stringify(discount) !== JSON.stringify(originalDiscount);
      
      setHasUnsavedChanges(productsChanged || discountChanged);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [productList, discount, originalProductList, originalDiscount, isReadOnly]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setProductList([]);
      setOriginalProductList([]);
      setShowProductCatalog(false);
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  const handleInvoiceClick = async () => {
    if (!orderData?._id) return;

    try {
      setLoading(true);
      const { data } = tokenDecode();
      const vendorId = data?.user?.vendor_id;

      if (!vendorId) {
        toast.error("Unable to fetch invoice");
        return;
      }

      const invoiceRes = await httpService.get(
        `/invoice/getorderinvoice?orderId=${orderData._id}&vendorId=${vendorId}`,
      );

      if (invoiceRes?.status && invoiceRes?.data?.invoice_url) {
        window.open(invoiceRes.data.invoice_url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Invoice not available");
      }
    } catch (err) {
      console.error("Failed fetching invoice", err);
      toast.error("Failed to fetch invoice");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    if (!orderData) return;

    const subtotal = parseFloat(orderData.amount_without_tax || 0);
    const gst = parseFloat(orderData.total_gst || 0);
    const total = parseFloat(orderData.amount_after_discount || 0);
    const discountAmount = parseFloat(orderData.discount_amount || 0);
    const grandTotal = parseFloat(orderData.grand_total || 0);

    const adjustmentAmount = parseFloat(orderData.adjustment_amount || 0);
    const adjusted = parseFloat(orderData.amount_after_adjustment || 0);
    const hasAdjustment = adjustmentAmount > 0 && adjusted > 0;
    const orderAmount = hasAdjustment ? adjusted : grandTotal;

    const paidAmount = parseFloat(orderData.total_paid_amount || 0);
    const waivedAmount = parseFloat(orderData.waived_amount || 0);
    const totalAlreadyPaid = paidAmount + waivedAmount;
    const remainingAmount = Math.max(0, grandTotal - totalAlreadyPaid);

    setTotalSummary({
      subtotal: subtotal.toFixed(2),
      gst: gst.toFixed(2),
      total: total.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
      orderAmount: orderAmount.toFixed(2),
      adjustment: (hasAdjustment ? adjustmentAmount : 0).toFixed(2),
      remainingAmount: remainingAmount.toFixed(2),
      paidAmount: paidAmount.toFixed(2),
      waivedAmount: waivedAmount.toFixed(2),
      discount: discountAmount.toFixed(2),
    });
  };

  // Fetch order and client data
  const fetchOrderAndClient = async () => {
    if (!isOpen || !orderId) return;
    try {
      setLoading(true);
      const orderRes = await httpService.get(`/vendororder/getvendororder/${orderId}`);
      const order = orderRes.data;
      
      const initialDiscount = { 
        type: order.discount_type,
        value: parseFloat(order.discount_amount || 0).toFixed(2),
        amount: parseFloat(order.discount_amount || 0).toFixed(2)
      };
      
      setDiscount(initialDiscount);
      setOriginalDiscount(initialDiscount);
      setOrderData(order);

      if (order.vendor_client_id) {
        try {
          const clientRes = await httpService.get(
            `/vendorclient/getvendorclient/${order.vendor_client_id}`
          );
          setClientData(clientRes.data);
        } catch (err) {
          console.error("Failed fetching client", err);
        }
      }
    } catch (err) {
      console.error("Failed fetching order", err);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderAndClient();
  }, [isOpen, orderId]);

  // Transform order items to product list
  useEffect(() => {
    if (orderData) {
      const itemsArray = orderData.items || orderData.order_items || [];

      const products = itemsArray
        .filter(item => item.item_type === "product" || item.type === "product")
        .map(item => ({
          _id: item._id,
          ref_id: item.ref_id,
          name: item.service_name || item.description || 'Product',
          unit_price: parseFloat(item.unit_price || item.price || 0),
          total_price: parseFloat(item.total_price || 0),
          quantity: parseInt(item.quantity) || 1,
          service_id: item.service_id || item.vendor_product_id,
          sku_number: item.sku_number || '',
          gst: parseFloat(item.gst) || 0,
          gst_rate: parseFloat(item.tax_percentage || item.gst_rate || 0),
          mrp: parseFloat(item.mrp || item.mrp_price || item.unit_price || 0),
          discount_value: parseFloat(item.discount_value || 0),
          discount_amount: parseFloat(item.discount_amount || 0),
          discount: 0,
        }));
      
      setProductList(products);
      setOriginalProductList([...products]);
    }
  }, [orderData]);

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (isReadOnly) return;
    
    if (newQuantity < 1) {
      toast.error("Quantity cannot be less than 1");
      return;
    }

    setProductList(prev => 
      prev.map(p => 
        p._id === productId 
          ? { 
              ...p, 
              quantity: newQuantity, 
              total_price: parseFloat(p.unit_price) * newQuantity 
            }
          : p
      )
    );
  };

  // Save all changes to backend
  const handleSaveChanges = async () => {
    if (isSaving || isReadOnly) return;
    setIsSaving(true);
    try {
      const activeProducts = productList.filter(product => 
        !product._markedForDeletion && product.quantity > 0
      );

      const orderItemsPayload = activeProducts.map(product => {
        const payloadItem = {
          item_type: 'product',
          service_id: product.service_id,
          description: product.name || '',
          sku_number: product.sku_number || '',
          quantity: product.quantity,
          unit_price: product.unit_price,
          gst: product.gst || 0,
          gst_rate: product.gst_rate || 0,
          total_price: product.total_price,
        };

        if (product._id && !product._id.startsWith('temp-')) {
          payloadItem._id = product._id;
          if (product.ref_id) {
            payloadItem.ref_id = product.ref_id;
          }
        }

        return payloadItem;
      });

      const updatePayload = {
        order_id: orderId,
        order_items: orderItemsPayload,
        total_discount: parseFloat(discount.value || 0),
        total_amount: parseFloat(totalSummary.grandTotal || 0),
        sub_total_amount: parseFloat(totalSummary.subtotal),
        total_gst: parseFloat(totalSummary.gst),
        vendor_client_id: orderData?.vendor_client_id || clientData?._id,
        waived_amount: parseFloat(orderData?.waived_amount || 0),
        round_off: orderData?.round_off || null,
      };
      
      const response = await httpService.post('/vendororder/addVendorOrderOrUpdate', updatePayload);
      
      if (response?.status) {
        toast.success('Changes saved successfully!', { position: 'top-center' });
        await new Promise(resolve => setTimeout(resolve, 500));

        const orderRes = await httpService.get(`/vendororder/getvendororder/${orderId}`);
        const order = orderRes.data;
        setOrderData(order);

        if (order.order_items) {
          const products = order.order_items
            .filter(item => item.item_type === "product")
            .map(item => ({
              _id: item._id,
              ref_id: item.ref_id,
              name: item.service_name,
              unit_price: parseFloat(item.unit_price) || 0,
              total_price: parseFloat(item.total_price) || 0,
              quantity: parseInt(item.quantity) || 1,
              service_id: item.service_id,
              sku_number: item.sku_number,
              gst: parseFloat(item.gst) || 0,
              gst_rate: 0,
              discount: 0,
            }));
          
          setProductList(products);
          setOriginalProductList([...products]);
        }

        if (order.vendor_client_id && !clientData) {
          try {
            const clientRes = await httpService.get(
              `/vendorclient/getvendorclient/${order.vendor_client_id}`
            );
            setClientData(clientRes.data);
          } catch (err) {
            console.error("Failed fetching client after update", err);
          }
        }

        setHasUnsavedChanges(false);

        if (refreshClients) {
          setTimeout(async () => {
            await refreshClients();
          }, 500);
        }
      } else {
        throw new Error('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes', { position: 'top-center' });
    } finally {
      setIsSaving(false);
    }
  };

  const validatePaymentStatus = (order) => {
    if (!order) return "unpaid";
    
    const totalAmount = parseFloat(order.total_amount || 0);
    const paidAmount = parseFloat(order.total_paid_amount || 0);
    const waivedAmount = parseFloat(order.waived_amount || 0);
    const roundOff = parseFloat(order.round_off || 0);
    
    const expectedTotal = totalAmount + roundOff;
    const totalCovered = paidAmount + waivedAmount;

    if (totalCovered >= expectedTotal) return "paid";
    else if (totalCovered > 0) return "partial";
    else return "unpaid";
  };

  const finalPaymentStatus = orderData?.payment_status || validatePaymentStatus(orderData);

  const handleProductSelect = (product) => {
    if (isReadOnly) return;
    
    const newProductPrice = parseFloat(product.unit_price || product.price || 0);
    const gstRate = parseFloat(product.gst_slab || 0);
    const itemGstAmount = gstRate > 0 ? (newProductPrice * gstRate) / 100 : 0;

    const existingProductIndex = productList.findIndex(p => 
      p.service_id === product.id || p.service_id === product._id
    );

    if (existingProductIndex !== -1) {
      const existingProduct = productList[existingProductIndex];
      const newQuantity = existingProduct.quantity + 1;
      const newTotalPrice = existingProduct.unit_price * newQuantity;
      
      setProductList(prev => 
        prev.map((p, index) => 
          index === existingProductIndex 
            ? { ...p, quantity: newQuantity, total_price: newTotalPrice }
            : p
        )
      );
      toast.success('Product quantity updated!', { position: 'top-center' });
    } else {
      const newProduct = {
        _id: `temp-${Date.now()}`,
        name: product.name,
        unit_price: newProductPrice,
        total_price: newProductPrice,
        quantity: 1,
        service_id: product.id || product._id,
        sku_number: product.sku_number || '',
        gst: itemGstAmount,
        gst_rate: gstRate,
        discount: 0,
      };
      setProductList(prev => [...prev, newProduct]);
      toast.success('Product added to order!', { position: 'top-center' });
    }
    
    setShowProductCatalog(false);
  };

  const handleClose = async () => {
    if (hasUnsavedChanges && !isReadOnly) {
      const result = await Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Do you want to save them before closing?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Save Changes",
        cancelButtonText: "Discard Changes",
      });

      if (result.isConfirmed) {
        await handleSaveChanges();
      }
    }

    onClose();

    try {
      if (refreshClients) {
        await refreshClients();
      }
    } catch (error) {
      console.error("Error refreshing data before closing:", error);
    }

    setTimeout(() => {
      setOrderData(null);
      setClientData(null);
      setProductList([]);
      setOriginalProductList([]);
      setShowProductCatalog(false);
      setHasUnsavedChanges(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Product Catalog Drawer */}
      {showProductCatalog && (
        <ProductCatalogDrawer
          isOpen={showProductCatalog}
          onClose={() => setShowProductCatalog(false)}
          onProductSelect={handleProductSelect}
          clientId={clientData?._id}
          orderId={orderId}
        />
      )}

      {/* Main Drawer */}
      {!showProductCatalog && (
        <div
          className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
            onClick={handleClose}
          />
          <div
            className={`absolute right-0 top-0 h-full w-full lg:w-2/5 bg-white shadow-xl transform transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            {/* Header */}
            <div className="flex justify-start items-center p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm gap-3">
              <button
                onClick={handleClose}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/70 transition-colors duration-200"
              >
                <HiArrowSmLeft className="w-6 h-6 text-gray-700" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <FiCalendar className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">
                  POS Details
                </h2>

                {orderData && (
                  <div className="flex items-center gap-2 ml-3">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs px-2.5 py-1 rounded-lg font-semibold">
                      #{orderData.order_number || orderId}
                    </div>

                    <div
                      className={`text-white text-xs px-2.5 py-1 rounded-lg font-semibold ${!orderData.order_type || orderData.order_type === "offline" ? "bg-gradient-to-r from-orange-500 to-red-500" : "bg-gradient-to-r from-green-500 to-emerald-500"}`}
                    >
                      {!orderData.order_type || orderData.order_type === "offline"
                        ? "Offline"
                        : "Online"}
                    </div>

                    <div
                      className={`text-white text-xs px-2.5 py-1 rounded-lg font-semibold ${
                        finalPaymentStatus === "paid"
                          ? "bg-gradient-to-r from-green-500 to-emerald-500"
                          : finalPaymentStatus === "partial"
                            ? "bg-gradient-to-r from-orange-500 to-yellow-500"
                            : "bg-gradient-to-r from-red-500 to-pink-500"
                      }`}
                    >
                      {finalPaymentStatus === "paid"
                        ? "Paid"
                        : finalPaymentStatus === "partial"
                          ? "Partial"
                          : "Unpaid"}
                    </div>

                    <div className="group relative">
                      <button
                        type="button"
                        className="p-2 rounded-md border bg-white transition-all duration-200 shadow-sm hover:shadow-md border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                        onClick={handleInvoiceClick}
                        title="View Invoice"
                      >
                        <FaFileInvoice className="text-gray-700" />
                      </button>
                      <span className="absolute z-10 top-10 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-50 text-gray-800 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-200 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
                        View Invoice
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loading && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/95">
                <div className="bg-white rounded-lg p-6 flex items-center gap-3 border border-gray-200 shadow-sm">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="text-gray-700">
                    Loading order details...
                  </span>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex h-[calc(100vh-73px)] flex-col">
              <div className="p-4 space-y-6 overflow-auto bg-gradient-to-b from-gray-50 to-white flex-1">
                {/* Client Card */}
                {clientData && (
                  <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
                    {/* Client card content - same as before */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                          <FiUser className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            {clientData.firstname} {clientData.lastname}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <FiPhone className="w-3 h-3" />
                              {clientData.phone}
                            </span>
                            {clientData.email && (
                              <span className="flex items-center gap-1">
                                <FiMail className="w-3 h-3" />
                                {clientData.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {clientData.address?.address1 && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 pl-16">
                          <FiMapPin className="w-3 h-3" />
                          <span>
                            {[
                              clientData.address.address1,
                              clientData.address.city,
                              clientData.address.state,
                              clientData.address.zip,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                          <button
                            onClick={() => {
                              const addressString = [
                                clientData.address.address1,
                                clientData.address.city,
                                clientData.address.state,
                                clientData.address.zip,
                              ]
                                .filter(Boolean)
                                .join(", ");
                              const destination =
                                clientData.address?.latitude &&
                                clientData.address?.longitude
                                  ? `${clientData.address.latitude},${clientData.address.longitude}`
                                  : encodeURIComponent(addressString);
                              const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

                              Swal.fire({
                                title: "Get Directions",
                                text: "Do you want to open Google Maps for directions?",
                                icon: "question",
                                showCancelButton: true,
                                confirmButtonColor: "#3085d6",
                                cancelButtonColor: "#d33",
                                confirmButtonText: "Yes",
                                cancelButtonText: "No",
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  window.open(googleMapsUrl, "_blank");
                                }
                              });
                            }}
                            className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-all duration-200 shadow hover:shadow-md flex-shrink-0"
                            title="Get Directions"
                          >
                            <FaDirections className="w-3 h-3" />
                            Directions
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Products Section */}
                <div className="mt-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                        <MdShoppingCart className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        Products
                      </h3>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {productList.length} {productList.length === 1 ? "item" : "items"}
                      </span>
                    </div>
                  </div>

                  <div className="p-2 space-y-4">
                    {productList.map((product, idx) => {
                      const unitPrice = parseFloat(product.unit_price || 0);
                      const mrp = parseFloat(product.mrp || product.mrp_price || unitPrice);
                      const qty = parseInt(product.quantity || 1);
                      const totalWithTax = parseFloat((unitPrice * qty).toFixed(2));
                      const gstRate = parseFloat(product.gst_rate || product.tax_percentage || 0);
                      const totalWithoutTax = gstRate > 0
                        ? parseFloat((totalWithTax / (1 + gstRate / 100)).toFixed(2))
                        : totalWithTax;
                      const taxAmount = parseFloat((totalWithTax - totalWithoutTax).toFixed(2));

                      const discountPercent = parseFloat(product.discount_value || product.discount_percent || 0);
                      const discountAmount = parseFloat(product.discount_amount || 0);

                      return (
                        <div
                          key={`${product.service_id}-${idx}`}
                          className={`grid grid-cols-12 gap-2 items-center p-4 rounded-lg border transition-colors duration-200 ${
                            product._markedForDeletion
                              ? "bg-red-50 border-red-200 opacity-60"
                              : isReadOnly
                                ? "bg-gray-50 border-gray-200"
                                : "bg-gray-50 border-gray-100 hover:bg-gray-100"
                          }`}
                        >
                          <div className="col-span-4 flex items-center gap-2">
                            <div className="min-w-0">
                              <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                                {product.name}
                                {gstRate > 0 && (
                                  <span className="ml-1 text-xs text-gray-400 font-normal">
                                    ({gstRate}% Tax)
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs text-gray-400 truncate">
                                SKU: {product.sku_number || "—"}
                              </p>
                            </div>
                          </div>

                          <div className="col-span-1 text-center">
                            <span className={`w-8 h-7 rounded-lg inline-flex items-center justify-center text-sm font-semibold ${
                              product._markedForDeletion
                                ? "bg-red-100 text-red-700"
                                : isReadOnly
                                  ? "bg-gray-100 text-gray-700"
                                  : "bg-blue-100 text-blue-700"
                            }`}>
                              {qty}
                            </span>
                          </div>

                          <div className="col-span-3 text-center">
                            <div className="text-sm font-semibold text-gray-800">
                              ₹{unitPrice.toFixed(2)}
                            </div>
                            {mrp > unitPrice && (
                              <div className="flex items-center justify-center gap-1">
                                <div className="text-xs text-gray-400 line-through">
                                  ₹{mrp.toFixed(2)}
                                </div>
                                {(discountPercent > 0 || discountAmount > 0) && (
                                  <div className="relative group">
                                    <div className="w-3.5 h-3.5 rounded-full bg-blue-100 flex items-center justify-center cursor-pointer">
                                      <span className="text-blue-600 text-xs font-bold leading-none">i</span>
                                    </div>
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-800 text-white text-[11px] rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 font-medium">
                                      Product Discount: {discountPercent}% | Saved: ₹{(discountAmount / qty).toFixed(2)}/unit
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="col-span-2 text-center">
                            <div className="text-sm font-bold text-gray-800">
                              ₹{totalWithTax.toFixed(2)}
                            </div>
                          </div>

                          <div className="col-span-2 text-right flex flex-col items-end gap-0.5">
                            <div className="text-sm font-semibold text-green-600">
                              ₹{totalWithoutTax.toFixed(2)}
                            </div>
                            {taxAmount > 0 && (
                              <div className="text-xs text-gray-400">
                                + ₹{taxAmount.toFixed(2)} Tax
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Totals Section */}
              {productList.length > 0 && (
                <div className="px-6 py-4 bg-white border-t border-gray-200">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>₹ {totalSummary.subtotal}</span>
                    </div>
                    {parseFloat(totalSummary.discount) > 0 && (
                      <div className="flex justify-between text-sm items-center">
                        <span>Discount</span>
                        <div className="flex gap-2 items-center">
                          <span className="text-xs text-gray-500">₹</span>
                          <input
                            type="number"
                            value={totalSummary.discount || ""}
                            onChange={(e) => {
                              if (isReadOnly) return;
                              const numericValue = parseFloat(e.target.value) || 0;
                              setDiscount((prev) => ({
                                ...prev,
                                type: "fixed",
                                value: numericValue,
                                amount: numericValue,
                              }));
                            }}
                            disabled={isReadOnly}
                            className={`border rounded px-2 py-1 text-xs w-20 text-right ${
                              isReadOnly
                                ? "bg-gray-100 cursor-not-allowed opacity-60"
                                : "bg-white"
                            }`}
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>Taxes</span>
                      <span>₹ {totalSummary.gst}</span>
                    </div>
                    {/* <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                      <span>Order Amount</span>
                      <span>₹ {totalSummary.grandTotal}</span>
                    </div> */}

                    {parseFloat(totalSummary.adjustment) > 0 && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Adjustment</span>
                        <span>- ₹ {totalSummary.adjustment}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                      <span>Order Amount</span>
                      <span>₹ {totalSummary.orderAmount}</span>
                    </div>
                    
                    {finalPaymentStatus === "partial" && (
                      <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                        <span>Amount Paid</span>
                        <span>₹ {parseFloat(orderData?.total_paid_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Section */}
              {productList.length > 0 && !isReadOnly && (
                  <PaymentAndInvoice
                    totals={{
                      subtotal: parseFloat(totalSummary.subtotal || 0),
                      disc: parseFloat(orderData?.total_discount || 0),
                      gst: parseFloat(totalSummary.gst || 0),
                      net: parseFloat(totalSummary.grandTotal || 0),
                      paid: parseFloat(orderData?.total_paid_amount || 0) + parseFloat(orderData?.waived_amount || 0),
                      remaining: parseFloat(totalSummary.remainingAmount || 0),
                    }}
                    orderData={{
                      ...orderData,
                      total_paid_amount: parseFloat(orderData?.total_paid_amount || 0),
                      amount_after_adjustment: parseFloat(orderData?.amount_after_adjustment || totalSummary.grandTotal || 0),
                      grand_total: parseFloat(orderData?.grand_total || totalSummary.grandTotal || 0),
                    }}
                    paymentStatus={finalPaymentStatus}
                    isOrderCancelled={isOrderCancelled}
                    isAnyAppointmentCancelled={false}
                    showPaymentSection={false}
                    isPaymentLoading={false}
                    paymentDetails={{}}
                    onPayNow={() => {
                      if (hasUnsavedChanges) {
                        handleSaveChanges().then(() => {
                          setTimeout(() => setPaymentOrderId(orderId), 500);
                        });
                      } else {
                        setPaymentOrderId(orderId);
                      }
                    }}
                    onBackFromPayment={() => {}}
                    onPaymentComplete={() => {}}
                    orderId={orderId}
                    vendorId={orderData?.vendor_id}
                    refreshOrderData={fetchOrderAndClient}
                  />
              )}

              {/* For paid orders */}
              {productList.length > 0 && isReadOnly && finalPaymentStatus === "paid" && (
                  <PaymentAndInvoice
                    totals={{
                      subtotal: parseFloat(totalSummary.subtotal || 0),
                      disc: parseFloat(orderData?.total_discount || 0),
                      gst: parseFloat(totalSummary.gst || 0),
                      net: parseFloat(totalSummary.grandTotal || 0),
                      paid: parseFloat(orderData?.total_paid_amount || 0) + parseFloat(orderData?.waived_amount || 0),
                      remaining: parseFloat(totalSummary.remainingAmount || 0),
                    }}
                    orderData={{
                      ...orderData,
                      total_paid_amount: parseFloat(orderData?.total_paid_amount || 0),
                      amount_after_adjustment: parseFloat(orderData?.amount_after_adjustment || totalSummary.grandTotal || 0),
                      grand_total: parseFloat(orderData?.grand_total || totalSummary.grandTotal || 0),
                    }}
                    paymentStatus={finalPaymentStatus}
                    isOrderCancelled={isOrderCancelled}
                    isAnyAppointmentCancelled={false}
                    showPaymentSection={false}
                    isPaymentLoading={false}
                    paymentDetails={{}}
                    onPayNow={() => {}}
                    onBackFromPayment={() => {}}
                    onPaymentComplete={() => {}}
                    orderId={orderId}
                    vendorId={orderData?.vendor_id}
                    refreshOrderData={fetchOrderAndClient}
                  />
              )}

              {/* For cancelled/view mode */}
              {isReadOnly && finalPaymentStatus !== "paid" && (
                <div className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex justify-center">
                    <button
                      onClick={handleClose}
                      className="flex-1 py-3 px-4 bg-gray-800 text-white font-semibold rounded-xl hover:bg-gray-700 transition-colors duration-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OrderPaymentDrawer */}
      <OrderPaymentDrawer
        isOpen={paymentOrderId !== null}
        onClose={() => {
          setPaymentOrderId(null);
          fetchOrderAndClient();
          if (refreshClients) {
            refreshClients();
          }
        }}
        orderId={paymentOrderId}
        onPaymentSuccess={(updatedOrder) => {
          setOrderData(updatedOrder);
          fetchOrderAndClient();
        }}
        isFullWidth={false}
      />
    </>
  );
};

export default POSDetailsDrawer;